import { ReactNode, useEffect, useMemo, useState } from 'react';
import { SidebarPinStateProvider } from '@backstage/core-components';
import { useNavCollapsed } from './state';

/**
 * The key and the format are core-components' own (`SidebarPage`), so the
 * choice made under the stock sidebar carries over.
 */
const PIN_STATE_KEY = 'sidebarPinState';

function readPinned(): boolean {
  try {
    return Boolean(JSON.parse(localStorage.getItem(PIN_STATE_KEY) ?? 'true'));
  } catch {
    return true;
  }
}

function writePinned(pinned: boolean) {
  try {
    localStorage.setItem(PIN_STATE_KEY, JSON.stringify(pinned));
  } catch {
    // Storage blocked: the choice lasts until the tab is closed.
  }
}

function Frame(props: { nav: ReactNode; content: ReactNode }) {
  const collapsed = useNavCollapsed();
  return (
    <div className="atlas-layout" data-collapsed={collapsed || undefined}>
      {props.nav}
      <div className="atlas-layout__content">{props.content}</div>
    </div>
  );
}

/**
 * The app's frame: the nav on the left, the page beside it. It replaces
 * core-components' `SidebarPage` and keeps the one thing other plugins read
 * from it, the pin state: Settings → Pin sidebar toggles it, and TechDocs
 * offsets its own drawer by the nav's width. `isMobile` is always false —
 * a narrow screen gets the icon rail, not core-components' bottom bar, so
 * nothing has to hide for it.
 */
export function Layout(props: { nav: ReactNode; content: ReactNode }) {
  const [isPinned, setIsPinned] = useState(readPinned);
  useEffect(() => writePinned(isPinned), [isPinned]);
  const pinState = useMemo(
    () => ({
      isPinned,
      toggleSidebarPinState: () => setIsPinned(pinned => !pinned),
      isMobile: false,
    }),
    [isPinned],
  );

  return (
    <SidebarPinStateProvider value={pinState}>
      <Frame nav={props.nav} content={props.content} />
    </SidebarPinStateProvider>
  );
}
